/**
 * Phase 7 Gelombang 2 — Management Actions tracker (per-scenario) + Rencana Manajemen widget.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ClipboardList, Plus } from "lucide-react";

const PRIORITY_COLOR = {
  CRITICAL: "bg-rose-100 text-rose-900 border-rose-200",
  HIGH:     "bg-amber-100 text-amber-900 border-amber-200",
  MEDIUM:   "bg-sky-100 text-sky-900 border-sky-200",
  LOW:      "bg-slate-100 text-slate-700 border-slate-200",
};
const STATUS_COLOR = {
  OPEN:        "bg-slate-100 text-slate-800 border-slate-200",
  IN_PROGRESS: "bg-amber-100 text-amber-900 border-amber-200",
  DONE:        "bg-emerald-100 text-emerald-900 border-emerald-200",
  CANCELLED:   "bg-rose-100 text-rose-900 border-rose-200",
};

const EDIT_ROLES = new Set(["super_admin", "direktur_utama", "direktur_operasional", "keuangan", "akuntansi"]);

export default function ManagementActionsPanel({ scenarioId, user }) {
  const canEdit = EDIT_ROLES.has(user?.role);
  const [actions, setActions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", owner: "", due_date: "", priority: "MEDIUM",
    linked_metric: "", note: "",
  });

  const load = () => {
    api.get(`/executive/management-actions?scenario_id=${scenarioId}`)
      .then(({ data }) => setActions(data.items || []))
      .catch(() => {});
  };
  useEffect(() => { if (scenarioId) load(); /* eslint-disable-next-line */ }, [scenarioId]);

  const create = async () => {
    if (!form.title || form.title.length < 3) {
      toast.error("Judul minimal 3 karakter.");
      return;
    }
    try {
      await api.post("/executive/management-actions", { ...form, scenario_id: scenarioId });
      toast.success("Aksi dibuat.");
      setOpen(false);
      setForm({ title: "", owner: "", due_date: "", priority: "MEDIUM", linked_metric: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (aid, status) => {
    try {
      await api.put(`/executive/management-actions/${aid}`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="management-actions-panel">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-sky-700" /> Rencana Aksi Manajemen
        </CardTitle>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-action-create">
                <Plus size={11} className="mr-1" />Tambah Aksi
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Tambah Aksi</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Judul</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                           data-testid="action-title" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Owner</Label>
                    <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}
                             data-testid="action-owner" />
                  </div>
                  <div>
                    <Label className="text-xs">Due date</Label>
                    <Input type="date" value={form.due_date}
                             onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                             data-testid="action-due" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Prioritas</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="action-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Linked metric (opsional)</Label>
                    <Input value={form.linked_metric}
                             onChange={(e) => setForm({ ...form, linked_metric: e.target.value })}
                             placeholder="revenue, ar_days, dsb"
                             data-testid="action-metric" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Catatan</Label>
                  <Textarea rows={2} value={form.note}
                              onChange={(e) => setForm({ ...form, note: e.target.value })}
                              data-testid="action-note" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} data-testid="action-submit">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Prioritas</TableHead>
              <TableHead>Judul</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Linked</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {actions.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-xs text-slate-400 italic py-6">
                Belum ada aksi tercatat
              </TableCell></TableRow>
            )}
            {actions.map((a) => (
              <TableRow key={a.id} data-testid={`act-row-${a.id}`}>
                <TableCell>
                  <Badge variant="outline" className={PRIORITY_COLOR[a.priority] || PRIORITY_COLOR.MEDIUM}>
                    {a.priority}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs font-semibold">{a.title}</TableCell>
                <TableCell className="text-xs">{a.owner || "—"}</TableCell>
                <TableCell className="text-xs">{a.due_date || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{a.linked_metric || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_COLOR[a.status] || STATUS_COLOR.OPEN}
                           data-testid={`act-status-${a.id}`}>
                    {a.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {canEdit && a.status !== "DONE" && a.status !== "CANCELLED" && (
                    <div className="flex gap-1">
                      {a.status === "OPEN" && (
                        <Button size="sm" variant="ghost" onClick={() => setStatus(a.id, "IN_PROGRESS")}
                                 data-testid={`act-start-${a.id}`}>Mulai</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setStatus(a.id, "DONE")}
                               data-testid={`act-done-${a.id}`}>Selesai</Button>
                      <Button size="sm" variant="ghost" onClick={() => setStatus(a.id, "CANCELLED")}
                               data-testid={`act-cancel-${a.id}`}>Batal</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function RencanaManajemenWidget() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get("/executive/active-plan-summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary({ available: false }));
  }, []);
  if (!summary) return null;
  if (!summary.available) {
    return (
      <Card data-testid="rencana-manajemen-widget-empty">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Rencana Manajemen</CardTitle></CardHeader>
        <CardContent>
          <div className="text-xs text-slate-400 italic">
            {summary.note || "Belum ada rencana aktif untuk periode ini"}
          </div>
          <Link to="/executive-scenarios" className="text-xs text-sky-700 hover:underline mt-2 inline-block">
            Buat skenario →
          </Link>
        </CardContent>
      </Card>
    );
  }
  const kpi = summary.kpi || {};
  const fmtRp = (x) => x == null ? "—" : `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(x))}`;
  return (
    <Card data-testid="rencana-manajemen-widget">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm">Rencana Manajemen Aktif</CardTitle>
        <Link to={`/executive-scenarios/${summary.plan.id}`} className="text-xs text-sky-700 hover:underline">
          Buka skenario →
        </Link>
      </CardHeader>
      <CardContent>
        <div className="text-xs text-slate-500 mb-1">
          {summary.plan.name} ({summary.plan.scenario_type}) · {summary.plan.year}-{String(summary.plan.month).padStart(2, "0")} ·
          <Badge variant="outline" className="ml-2 text-[10px]">{summary.plan.status}</Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          {[["Omzet", kpi.revenue], ["Gross Profit", kpi.gross_profit],
             ["Net Profit", kpi.net_profit], ["Ending Cash", kpi.ending_cash],
             ["Working Capital", kpi.working_capital_last]].map(([k, v]) => (
            <div key={k} className="rounded bg-slate-50 p-2 text-xs">
              <div className="text-[10px] text-slate-500">{k}</div>
              <div className="font-semibold">{fmtRp(v)}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-semibold mb-1">Top Risiko</div>
            {(summary.risks || []).length === 0 ? (
              <div className="text-xs text-slate-400 italic">Tidak ada risiko aktif</div>
            ) : (
              <ul className="text-xs space-y-1">
                {(summary.risks || []).slice(0, 3).map((r, i) => (
                  <li key={i} className="flex gap-2">
                    <Badge variant="outline" className={r.severity === "CRITICAL" ? "bg-rose-100 text-rose-900 border-rose-200 text-[10px]" : "bg-amber-100 text-amber-900 border-amber-200 text-[10px]"}>
                      {r.severity}
                    </Badge>
                    <span className="truncate">{r.alert_code}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <div className="text-xs font-semibold mb-1">Top Aksi</div>
            {(summary.actions || []).length === 0 ? (
              <div className="text-xs text-slate-400 italic">Belum ada aksi</div>
            ) : (
              <ul className="text-xs space-y-1">
                {(summary.actions || []).slice(0, 3).map((a) => (
                  <li key={a.id} className="flex gap-2">
                    <Badge variant="outline" className={PRIORITY_COLOR[a.priority] || PRIORITY_COLOR.MEDIUM}>{a.priority}</Badge>
                    <span className="truncate">{a.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
